/** @forge-header
 * @id          kalshiApiOrderbook
 * @role        Public market data endpoints | orderbook depth, OHLC candles, trade tape
 * @pattern     adapter
 * @purity      api
 * @units       cents
 * @upstream    kalshiApi/client:authenticatedFetch | kalshiApi/types:KalshiCandlestick,KalshiTrade
 * @downstream  kalshiApi/index (via createKalshiClient)
 * @exports     getOrderbook:fn | getCandlesticks:fn | getMarketTrades:fn
 * @test        untested (integration-level)
 */

import type { KalshiOrderbook } from '../../types';
import type { KalshiCandlestick, KalshiTrade } from './types';
import { authenticatedFetch } from './client';

/**
 * Get orderbook for a market
 */
export async function getOrderbook(marketTicker: string): Promise<KalshiOrderbook | null> {
  try {
    const response = await authenticatedFetch(
      'GET',
      `/trade-api/v2/markets/${marketTicker}/orderbook`
    );
    const data = await response.json();
    // API returns { orderbook: { yes, no } } wrapper — unwrap like getEvent() does
    const ob = data?.orderbook ?? data;
    return ob as KalshiOrderbook;
  } catch (error) {
    console.warn(`Failed to fetch orderbook for ${marketTicker}:`, error);
    return null;
  }
}

/**
 * Get candlesticks for a market
 */
export async function getCandlesticks(
  seriesTicker: string,
  marketTicker: string,
  params: { start_ts: number; end_ts: number; period_interval: 1 | 60 | 1440 }
): Promise<KalshiCandlestick[]> {
  try {
    const query: Record<string, string> = {
      start_ts: params.start_ts.toString(),
      end_ts: params.end_ts.toString(),
      period_interval: params.period_interval.toString(),
    };

    const response = await authenticatedFetch(
      'GET',
      `/trade-api/v2/series/${seriesTicker}/markets/${marketTicker}/candlesticks`,
      query
    );
    const data = await response.json();

    if (data.candlesticks && Array.isArray(data.candlesticks)) {
      return data.candlesticks;
    }

    return [];
  } catch (error) {
    console.warn(`Failed to fetch candlesticks for ${marketTicker}:`, error);
    return [];
  }
}

/**
 * Get market trades (public trade tape)
 */
export async function getMarketTrades(
  params: { ticker?: string; cursor?: string; limit?: number } = {}
): Promise<KalshiTrade[]> {
  try {
    const query: Record<string, string> = {};
    if (params.ticker) query.ticker = params.ticker;
    if (params.cursor) query.cursor = params.cursor;
    if (params.limit) query.limit = params.limit.toString();

    const response = await authenticatedFetch('GET', '/trade-api/v2/markets/trades', query);
    const data = await response.json();

    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    }
    if (data.trades && Array.isArray(data.trades)) {
      return data.trades;
    }
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }

    return [];
  } catch (error) {
    console.warn('Failed to fetch market trades:', error);
    return [];
  }
}
